import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { Check, ChevronRight, Pencil, Images, Layers, Users, Plus, UserX, Eye, Download, Star, Wifi, Flag, MonitorPlay, BarChart3, ScanFace, Lock, Users2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Input, Textarea } from '../UI/Input';
import { TabItem } from '../UI/TabBar';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { OverlayConfigCard } from './OverlayConfigCard';
import { DomainRestrictionField } from './DomainRestrictionField';
import { getReservedAnchors, pickFreeAnchor } from '../../lib/overlayAnchors';

const DEFAULT_OVERLAY_S3_PATH = 'assets/overlays/whitefont.png';
import { AlbumPhotoCanvas, AlbumPhotoCanvasHandle } from './AlbumPhotoCanvas';
import { albumsApi } from '../../api/albums';
import { photosApi } from '../../api/photos';
import { overlaysApi } from '../../api/overlays';
import { brandingApi, BrandingDefaultsConfig } from '../../api/settings';
import { Album, AlbumMember, AlbumOverlayConfig, FTPAccount, Photo, Overlay } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { ftpAccountsApi } from '../../api/ftpAccounts';
import { track, EVENTS } from '../../lib/mixpanel';
import { Skeleton } from '../UI/Skeleton';
import { ReportsPanel } from './ReportsPanel';
import { SlideshowSettingsTab } from './SlideshowSettingsTab';
import { AnalyticsTab } from './AnalyticsTab';
import faceStyles from './FaceRecognitionStep.module.css';
import styles from './AlbumEditModal.module.css';

type EditTab = 'info' | 'photos' | 'cover' | 'overlays' | 'face' | 'sources' | 'members' | 'reports' | 'slideshow' | 'analytics';

const NORMAL_TABS: TabItem<EditTab>[] = [
  { id: 'info',     label: 'Info',     icon: Pencil   },
  { id: 'photos',   label: 'Photos',   icon: Images   },
  { id: 'cover',    label: 'Cover',    icon: Star     },
  { id: 'overlays', label: 'Overlays', icon: Layers   },
  { id: 'face',     label: 'Face',     icon: ScanFace },
  { id: 'members',  label: 'Members',  icon: Users    },
  { id: 'reports',  label: 'Reports',  icon: Flag     },
];

const LIVE_TABS: TabItem<EditTab>[] = [
  { id: 'info',       label: 'Info',          icon: Pencil      },
  { id: 'cover',      label: 'Cover',         icon: Star        },
  { id: 'overlays',   label: 'Overlays',      icon: Layers      },
  { id: 'face',       label: 'Face',          icon: ScanFace    },
  { id: 'sources',    label: 'Sources',       icon: Wifi        },
  { id: 'analytics',  label: 'Analytics',     icon: BarChart3   },
  { id: 'slideshow',  label: 'Venue Display', icon: MonitorPlay },
  { id: 'members',    label: 'Members',       icon: Users       },
  { id: 'reports',    label: 'Reports',       icon: Flag        },
];

// ─── Tab stepper ──────────────────────────────────────────────────────────────

function EditTabStepper({
  tabs,
  activeTab,
  onChange,
}: {
  tabs: TabItem<EditTab>[];
  activeTab: EditTab;
  onChange: (tab: EditTab) => void;
}) {
  return (
    <div className={styles['tab-stepper']}>
      {tabs.map((tab, i) => {
        const active = activeTab === tab.id;
        const Icon = tab.icon;
        return (
          <React.Fragment key={tab.id}>
            <button
              onClick={() => onChange(tab.id)}
              className={styles['tab-btn']}
            >
              <div className={[
                styles['tab-dot'],
                active ? styles['tab-dot-active'] : styles['tab-dot-inactive'],
              ].join(' ')}>
                <Icon style={{ width: 14, height: 14 }} />
              </div>
              <span className={[
                styles['tab-label'],
                active ? styles['tab-label-active'] : styles['tab-label-inactive'],
              ].join(' ')}>
                {tab.label}
              </span>
            </button>

            {/* Connector line */}
            {i < tabs.length - 1 && (
              <div className={styles['tab-connector']} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Full-screen overlay preview ──────────────────────────────────────────────

function OverlayPreviewModal({
  isOpen,
  onClose,
  photo,
  overlayConfigs,
}: {
  isOpen: boolean;
  onClose: () => void;
  photo: Photo;
  overlayConfigs: AlbumOverlayConfig[];
}) {
  const canvasRef = useRef<AlbumPhotoCanvasHandle>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useEffect(() => {
    if (!isOpen) return;
    const measure = () => {
      const vw = Math.min(window.innerWidth * 0.9, 1200);
      const vh = window.innerHeight * 0.8;
      const aspect = 4 / 3;
      let w = vw;
      let h = w / aspect;
      if (h > vh) { h = vh; w = h * aspect; }
      setSize({ w: Math.round(w), h: Math.round(h) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles['preview-overlay']}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <motion.div className={styles['preview-backdrop']} onClick={onClose} />
          <motion.div
            className={styles['preview-content']}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          >
            <div className={styles['preview-canvas-wrap']}>
              {size.w > 0 && (
                <AlbumPhotoCanvas
                  ref={canvasRef}
                  photo={photo}
                  overlayConfigs={overlayConfigs}
                  width={size.w}
                  height={size.h}
                  useOriginal={false}
                />
              )}
            </div>
            <div className={styles['preview-actions']}>
              <p className={styles['preview-caption']}>
                This is how the overlay will appear on photos in your album.
              </p>
              <button
                onClick={() => canvasRef.current?.downloadCanvas()}
                className={styles['preview-action-btn']}
              >
                <Download style={{ width: 14, height: 14 }} />
                Save Preview
              </button>
              <button
                onClick={onClose}
                className={styles['preview-action-btn']}
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Info tab ─────────────────────────────────────────────────────────────────

function InfoTab({
  album,
  onUpdated,
}: {
  album: Album;
  onUpdated: (updated: Album) => void;
}) {
  const { user } = useAuthStore();
  const [name, setName] = useState(album.name);
  const [description, setDescription] = useState(album.description);
  const [editAccess, setEditAccess] = useState<'team' | 'creator_only'>(album.editAccess ?? 'team');
  const [allowedEmailDomains, setAllowedEmailDomains] = useState<string[]>(album.allowedEmailDomains ?? []);
  const [saving, setSaving] = useState(false);

  const createdById = typeof album.createdBy === 'object' ? album.createdBy._id : album.createdBy;
  const isAlbumCreator = user?._id === createdById;

  const initialDomains = album.allowedEmailDomains ?? [];
  const domainsChanged =
    [...allowedEmailDomains].sort().join(',') !== [...initialDomains].sort().join(',');

  const isDirty =
    name.trim() !== album.name ||
    description.trim() !== album.description ||
    editAccess !== (album.editAccess ?? 'team') ||
    domainsChanged;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await albumsApi.update(album._id, {
        name: name.trim(),
        description: description.trim(),
        ...(isAlbumCreator && { editAccess }),
        ...(domainsChanged && { allowedEmailDomains }),
      });
      onUpdated(updated);
      toast.success('Album updated');
    } catch {
      toast.error('Failed to update album');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles['tab-col']}>
      <Input
        label="Album Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={80}
        required
      />
      <Textarea
        label="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
      />

      {/* Edit access — only shown to the album creator */}
      {isAlbumCreator && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>
            Edit access
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setEditAccess('team')}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                borderRadius: 12,
                border: `2px solid ${editAccess === 'team' ? 'var(--clay-orange)' : 'var(--border-subtle)'}`,
                background: editAccess === 'team' ? 'rgba(239,68,68,0.06)' : 'var(--bg-card)',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <Users2 style={{ width: 14, height: 14, color: editAccess === 'team' ? 'var(--clay-orange)' : 'var(--text-muted)', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <p style={{ fontSize: 13, fontWeight: 600, margin: 0, color: editAccess === 'team' ? 'var(--clay-orange)' : 'var(--text-primary)' }}>Team</p>
                <p style={{ fontSize: 11, margin: 0, color: 'var(--text-muted)' }}>Any team member can edit</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setEditAccess('creator_only')}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                borderRadius: 12,
                border: `2px solid ${editAccess === 'creator_only' ? 'var(--clay-orange)' : 'var(--border-subtle)'}`,
                background: editAccess === 'creator_only' ? 'rgba(239,68,68,0.06)' : 'var(--bg-card)',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <Lock style={{ width: 14, height: 14, color: editAccess === 'creator_only' ? 'var(--clay-orange)' : 'var(--text-muted)', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <p style={{ fontSize: 13, fontWeight: 600, margin: 0, color: editAccess === 'creator_only' ? 'var(--clay-orange)' : 'var(--text-primary)' }}>Only me</p>
                <p style={{ fontSize: 11, margin: 0, color: 'var(--text-muted)' }}>Only I can edit this album</p>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Access restriction — limit joins to specific email domains */}
      <DomainRestrictionField value={allowedEmailDomains} onChange={setAllowedEmailDomains} />

      <div className={styles['tab-footer']}>
        <Button onClick={handleSave} loading={saving} disabled={!isDirty || !name.trim()}>
          Save
        </Button>
      </div>
    </div>
  );
}

// ─── Photos tab ───────────────────────────────────────────────────────────────

function PhotosTab({
  album,
  onUpdated: _onUpdated,
}: {
  album: Album;
  onUpdated: (updated: Album) => void;
}) {
  const [allPhotos, setAllPhotos] = useState<Photo[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  // Refs for infinite scroll inside the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Set of photo IDs currently in album
  const [inAlbum, setInAlbum] = useState<Set<string>>(new Set());
  // Pending selection (starts as inAlbum, toggled by user)
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      photosApi.list(album.eventId),
      albumsApi.listPhotos(album._id),
    ])
      .then(([eventPhotos, albumPhotos]) => {
        cursorRef.current = eventPhotos.nextCursor;
        setAllPhotos(eventPhotos.photos);
        setNextCursor(eventPhotos.nextCursor);
        setHasMore(eventPhotos.hasMore);
        const ids = new Set(albumPhotos.photos.map((p) => p._id));
        setInAlbum(ids);
        setSelected(new Set(ids));
      })
      .catch(() => toast.error('Failed to load photos'))
      .finally(() => setLoading(false));
  // album._id is the only meaningful dep — re-fetch when a different album is opened
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [album._id]);

  const loadMore = useCallback(async () => {
    const cursor = cursorRef.current;
    if (!cursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const r = await photosApi.list(album.eventId, cursor);
      cursorRef.current = r.nextCursor;
      setAllPhotos((prev) => [...prev, ...r.photos]);
      setNextCursor(r.nextCursor);
      setHasMore(r.hasMore);
    } catch {
      toast.error('Failed to load more photos');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [album.eventId]);

  // Keep a stable ref to loadMore so observer callbacks never go stale
  const loadMoreFnRef = useRef(loadMore);
  useEffect(() => { loadMoreFnRef.current = loadMore; }, [loadMore]);

  // Callback ref — sets up IntersectionObserver on the sentinel element
  // using the scrollable container as the root so it fires within the modal
  const sentinelCallbackRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreFnRef.current(); },
      { root: scrollContainerRef.current, rootMargin: '200px' },
    );
    observerRef.current.observe(el);
  }, []);

  const togglePhoto = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const allSelected = allPhotos.length > 0 && allPhotos.every((p) => selected.has(p._id)) && !hasMore;
  const someSelected = selected.size > 0;

  const handleSelectAll = async () => {
    if (allSelected) {
      setSelected(new Set());
      return;
    }

    if (!hasMore) {
      const allCurrent = allPhotos.every((p) => selected.has(p._id));
      if (allCurrent) {
        setSelected(new Set());
      } else {
        setSelected(new Set(allPhotos.map((p) => p._id)));
      }
      return;
    }

    // Load all remaining pages first
    setSelectingAll(true);
    let all = [...allPhotos];
    let cursor = nextCursor;
    try {
      while (cursor) {
        const r = await photosApi.list(album.eventId, cursor);
        all = [...all, ...r.photos];
        cursor = r.nextCursor ?? null;
        cursorRef.current = r.nextCursor;
        setNextCursor(r.nextCursor);
        setHasMore(r.hasMore);
      }
      setAllPhotos(all);
      setSelected(new Set(all.map((p) => p._id)));
    } catch {
      toast.error('Failed to load all photos');
    } finally {
      setSelectingAll(false);
    }
  };

  const isDirty = (() => {
    if (inAlbum.size !== selected.size) return true;
    for (const id of selected) if (!inAlbum.has(id)) return true;
    return false;
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      const added = [...selected].filter((id) => !inAlbum.has(id));
      const removed = [...inAlbum].filter((id) => !selected.has(id));

      if (added.length > 0) await albumsApi.addPhotos(album._id, added);
      for (const id of removed) await albumsApi.removePhoto(album._id, id);

      // Re-fetch from server to confirm persisted state (avoids optimistic drift)
      const confirmed = await albumsApi.listPhotos(album._id);
      const confirmedIds = new Set(confirmed.photos.map((p) => p._id));
      setInAlbum(confirmedIds);
      setSelected(new Set(confirmedIds));
      toast.success('Photos updated');
    } catch {
      toast.error('Failed to update photos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '1rem' }}>
        <div className={styles['photo-grid']}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} style={{ aspectRatio: '1', borderRadius: 'var(--radius-clay-sm)' }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles['photos-col']}>
      {/* Toolbar */}
      <div className={styles['toolbar']}>
        <button
          onClick={handleSelectAll}
          disabled={selectingAll || loading}
          className={styles['select-all-btn']}
        >
          <div className={[
            styles['checkbox-box'],
            allSelected ? styles['checkbox-all'] : someSelected ? styles['checkbox-some'] : '',
          ].filter(Boolean).join(' ')}>
            {allSelected && <Check style={{ width: 10, height: 10, color: '#fff' }} strokeWidth={3} />}
            {!allSelected && someSelected && <div className={styles['checkbox-dash']} />}
          </div>
          {selectingAll ? 'Loading all\u2026' : allSelected ? 'Deselect All' : 'Select All'}
        </button>
        <span className={styles['selected-count']}>
          {selected.size} in album{hasMore ? ' (more to load)' : ''}
        </span>
      </div>

      {/* Grid */}
      <div ref={scrollContainerRef} className={styles['photos-scroll']}>
        {allPhotos.length === 0 ? (
          <div className={styles['empty-state']}>
            <MachhaCharacter state="Empty" size={80} />
            <p className={styles['empty-text']}>No photos uploaded</p>
          </div>
        ) : (
          <>
            <div className={styles['photo-grid']}>
              {allPhotos.map((photo) => {
                const isSelected = selected.has(photo._id);
                const wasInAlbum = inAlbum.has(photo._id);
                return (
                  <button
                    key={photo._id}
                    onClick={() => togglePhoto(photo._id)}
                    aria-label={`${isSelected ? 'Remove' : 'Add'} ${photo.originalFilename}`}
                    aria-pressed={isSelected}
                    className={[
                      styles['photo-cell'],
                      isSelected ? styles['photo-cell-selected'] : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {photo.thumbnailUrl ? (
                      <img
                        src={photo.thumbnailUrl}
                        alt={photo.originalFilename}
                        className={styles['img-cover']}
                        loading="lazy"
                      />
                    ) : (
                      <div className={styles['img-placeholder']} />
                    )}
                    {isSelected && (
                      <div className={styles['check-badge']}>
                        {wasInAlbum
                          ? <Check style={{ width: 12, height: 12, color: '#fff' }} strokeWidth={3} />
                          : <Plus style={{ width: 12, height: 12, color: '#fff' }} strokeWidth={3} />
                        }
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Infinite-scroll sentinel */}
            {hasMore && <div ref={sentinelCallbackRef} className={styles['sentinel']} />}

            {loadingMore && (
              <div className={styles['photo-grid']} style={{ marginTop: '0.5rem' }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} style={{ aspectRatio: '1', borderRadius: 'var(--radius-clay-sm)' }} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className={styles['tab-footer']}>
        <Button onClick={handleSave} loading={saving} disabled={!isDirty}>
          Save Changes
        </Button>
      </div>
    </div>
  );
}

// ─── Cover tab ───────────────────────────────────────────────────────────────

function CoverTab({
  album,
  onUpdated,
}: {
  album: Album;
  onUpdated: (updated: Album) => void;
}) {
  const [albumPhotos, setAlbumPhotos] = useState<Photo[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [coverPhotoId, setCoverPhotoId] = useState<string | null>(album.coverPhotoId ?? null);
  const [saving, setSaving] = useState(false);

  // Infinite scroll inside the modal's scroll container — load one page at a
  // time as the user scrolls, instead of blocking on the whole album up front
  // (large albums previously stalled this tab on a long chain of requests).
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // First page — render as soon as it arrives.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCoverPhotoId(album.coverPhotoId ?? null);
    setAlbumPhotos([]);
    cursorRef.current = null;

    albumsApi.listPhotos(album._id)
      .then((r) => {
        if (cancelled) return;
        setAlbumPhotos(r.photos);
        cursorRef.current = r.nextCursor;
        setHasMore(!!r.nextCursor);
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load album photos'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [album._id]);

  const loadMore = useCallback(async () => {
    const cursor = cursorRef.current;
    if (!cursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const r = await albumsApi.listPhotos(album._id, cursor);
      cursorRef.current = r.nextCursor;
      setAlbumPhotos((prev) => [...prev, ...r.photos]);
      setHasMore(!!r.nextCursor);
    } catch {
      toast.error('Failed to load more photos');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [album._id]);

  // Stable ref so the observer callback never goes stale.
  const loadMoreFnRef = useRef(loadMore);
  useEffect(() => { loadMoreFnRef.current = loadMore; }, [loadMore]);

  // Callback ref — attaches an IntersectionObserver to the bottom sentinel,
  // rooted on the scroll container so it fires within the modal.
  const sentinelCallbackRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreFnRef.current(); },
      { root: scrollContainerRef.current, rootMargin: '200px' },
    );
    observerRef.current.observe(el);
  }, []);

  const isDirty = coverPhotoId !== (album.coverPhotoId ?? null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await albumsApi.update(album._id, { coverPhotoId: coverPhotoId ?? null });
      onUpdated(updated);
      toast.success('Cover photo updated');
    } catch {
      toast.error('Failed to update cover photo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '1rem' }}>
        <div className={styles['cover-grid']}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} style={{ aspectRatio: '1', borderRadius: 'var(--radius-clay-sm)' }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles['tab-col']}>
      <p className={styles['cover-hint']}>
        Pick a cover photo shown (blurred) to guests on the join page.
        Click the same photo again to clear.
      </p>

      <div ref={scrollContainerRef} className={styles['tab-scroll-tall']}>
        {albumPhotos.length === 0 ? (
          <div className={styles['empty-state']}>
            <MachhaCharacter state="Empty" size={80} />
            <p className={styles['empty-text']}>
              No photos in this album — add photos in the Photos tab first.
            </p>
          </div>
        ) : (
          <>
            <div className={styles['cover-grid']}>
              {albumPhotos.map((photo) => {
                const isCover = coverPhotoId === photo._id;
                return (
                  <button
                    key={photo._id}
                    onClick={() => setCoverPhotoId(isCover ? null : photo._id)}
                    aria-label={isCover ? `Remove cover: ${photo.originalFilename}` : `Set as cover: ${photo.originalFilename}`}
                    aria-pressed={isCover}
                    className={[
                      styles['cover-cell'],
                      isCover ? styles['cover-cell-active'] : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {photo.thumbnailUrl ? (
                      <img
                        src={photo.thumbnailUrl}
                        alt={photo.originalFilename}
                        className={styles['img-cover']}
                        loading="lazy"
                      />
                    ) : (
                      <div className={styles['img-placeholder']} />
                    )}

                    {/* Cover overlay */}
                    {isCover && (
                      <div className={styles['cover-overlay']}>
                        <div className={styles['cover-badge']}>
                          <Star style={{ width: 10, height: 10, color: '#fff', fill: '#fff' }} />
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Infinite-scroll sentinel — loads the next page as it nears view */}
            {hasMore && <div ref={sentinelCallbackRef} className={styles['sentinel']} />}

            {loadingMore && (
              <div className={styles['cover-grid']} style={{ marginTop: '0.5rem' }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} style={{ aspectRatio: '1', borderRadius: 'var(--radius-clay-sm)' }} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {coverPhotoId && (
        <p className={styles['cover-set-msg']}>
          &#9733; Cover photo set
          <button
            onClick={() => setCoverPhotoId(null)}
            className={styles['cover-clear-btn']}
          >
            Clear
          </button>
        </p>
      )}

      <div className={styles['tab-footer']}>
        <Button onClick={handleSave} loading={saving} disabled={!isDirty}>
          Save Cover
        </Button>
      </div>
    </div>
  );
}

// ─── Overlays tab ─────────────────────────────────────────────────────────────

function OverlaysTab({
  album,
  onUpdated,
}: {
  album: Album;
  onUpdated: (updated: Album) => void;
}) {
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [loadingOverlays, setLoadingOverlays] = useState(false);
  const [overlayConfigs, setOverlayConfigs] = useState<AlbumOverlayConfig[]>(album.overlayConfigs);
  const [saving, setSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<Photo | null>(null);
  const [loadingPreviewPhoto, setLoadingPreviewPhoto] = useState(false);
  const [brandingDefaults, setBrandingDefaults] = useState<BrandingDefaultsConfig | null>(null);

  const isOwner = useAuthStore((s) => s.user?.isOwner ?? false);

  useEffect(() => {
    if (initialized) return;
    setLoadingOverlays(true);
    overlaysApi.list(album.eventId)
      .then((data) => {
        setOverlays(data);
        setInitialized(true);
      })
      .catch(() => toast.error('Failed to load overlays'))
      .finally(() => setLoadingOverlays(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Owners can re-attach the default watermark after removing it, so we need
  // the system anchor/size/opacity to rebuild its config.
  useEffect(() => {
    if (!isOwner) return;
    brandingApi.getDefaults()
      .then((d) => setBrandingDefaults(d.config))
      .catch(() => {/* non-critical — falls back to hardcoded defaults */});
  }, [isOwner]);

  const addedIds = new Set(overlayConfigs.map((c) => c.overlayId));
  // The default Pixel Machha watermark is auto-injected for non-owner users.
  // We hide it from the "Add overlay" strip and the configured-overlays list,
  // but still treat its anchor as reserved for any additional overlays.
  const defaultOverlayIdSet = new Set(
    overlays.filter((o) => o.storagePath === DEFAULT_OVERLAY_S3_PATH).map((o) => o._id),
  );
  const defaultOverlay = overlays.find((o) => o.storagePath === DEFAULT_OVERLAY_S3_PATH) ?? null;
  const availableToAdd = overlays.filter(
    (o) => !addedIds.has(o._id) && !defaultOverlayIdSet.has(o._id),
  );

  // Existing default-overlay configs (server-injected). Hidden from the
  // visible list but their anchors count as reserved.
  const visibleConfigs = overlayConfigs.filter((c) => !defaultOverlayIdSet.has(c.overlayId));
  const defaultOverlayConfigs = overlayConfigs.filter((c) => defaultOverlayIdSet.has(c.overlayId));
  const defaultBranded = defaultOverlayConfigs.length > 0;
  const defaultAnchor = defaultOverlayConfigs[0]?.anchor ?? null;

  const addOverlay = (overlay: Overlay) =>
    setOverlayConfigs((prev) => {
      const reserved = getReservedAnchors(prev, -1);
      const anchor = pickFreeAnchor(reserved);
      return [
        ...prev,
        { overlayId: overlay._id, anchor, size: 'md', opacity: 1 },
      ];
    });

  const updateConfig = (visibleIndex: number, updated: AlbumOverlayConfig) =>
    setOverlayConfigs((prev) => {
      // Map visibleIndex back to the absolute index in `prev` (which contains
      // hidden default-overlay entries too)
      const visible = prev.filter((c) => !defaultOverlayIdSet.has(c.overlayId));
      const target = visible[visibleIndex];
      return prev.map((c) => (c === target ? updated : c));
    });

  const removeConfig = (visibleIndex: number) =>
    setOverlayConfigs((prev) => {
      const visible = prev.filter((c) => !defaultOverlayIdSet.has(c.overlayId));
      const target = visible[visibleIndex];
      return prev.filter((c) => c !== target);
    });

  // Owner-only: attach/detach the default Pixel Machha watermark. Non-owners
  // can't reach this — the server also rejects removal for them.
  const toggleDefaultBranding = (next: boolean) => {
    if (!defaultOverlay) return;
    setOverlayConfigs((prev) => {
      if (!next) return prev.filter((c) => !defaultOverlayIdSet.has(c.overlayId));
      if (prev.some((c) => defaultOverlayIdSet.has(c.overlayId))) return prev;
      const reserved = getReservedAnchors(prev, -1);
      const preferred = brandingDefaults?.anchor;
      const anchor = preferred && !reserved.has(preferred) ? preferred : pickFreeAnchor(reserved);
      return [
        ...prev,
        {
          overlayId: defaultOverlay._id,
          anchor,
          size: brandingDefaults?.size ?? 'sm',
          opacity: brandingDefaults?.opacity ?? 1,
        },
      ];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await albumsApi.update(album._id, { overlayConfigs });
      onUpdated(updated);
      toast.success('Overlays saved');
    } catch {
      toast.error('Failed to save overlays');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles['tab-col']}>
      <div className={styles['tab-scroll']}>
        {/* Add overlay strip */}
        {loadingOverlays ? (
          <div className={styles['overlay-skeleton-row']}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ width: 56, height: 56, borderRadius: 8 }} />
            ))}
          </div>
        ) : overlays.length === 0 ? (
          <div className={styles['overlay-empty-msg']}>
            No overlays uploaded for this event.
          </div>
        ) : availableToAdd.length > 0 ? (
          <div>
            <p className={styles['overlay-add-label']}>Add Overlay</p>
            <div className={styles['overlay-strip']}>
              {availableToAdd.map((overlay) => (
                <button
                  key={overlay._id}
                  onClick={() => addOverlay(overlay)}
                  aria-label={`Add overlay ${overlay.name}`}
                  className={styles['overlay-add-btn']}
                >
                  <div className={styles['overlay-add-thumb']}>
                    <img
                      src={overlaysApi.fileUrl(overlay._id)}
                      alt={overlay.name}
                      className={styles['img-contain']}
                    />
                    <div className={styles['overlay-add-hover-mask']}>
                      <Plus style={{ width: 16, height: 16, color: 'var(--clay-orange)' }} />
                    </div>
                  </div>
                  <span className={styles['overlay-add-name']}>
                    {overlay.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Configured overlays */}
        {visibleConfigs.length === 0 ? (
          <div className={styles['overlay-none-msg']}>
            {defaultBranded
              ? 'Only the default Pixel Machha watermark is applied. Add another overlay above to layer it.'
              : 'No overlays configured — photos will be shown without overlays'}
          </div>
        ) : (
          <div className={styles['overlay-configs-col']}>
            {visibleConfigs.map((config, i) => {
              const overlay = overlays.find((o) => o._id === config.overlayId);
              if (!overlay) return null;
              // Build reserved set from sibling visible configs + the default
              // overlay anchor (if branded). The card's own anchor is excluded
              // by passing the visible index and computing siblings.
              const reservedAnchors = new Set<typeof config.anchor>();
              visibleConfigs.forEach((c, j) => { if (j !== i) reservedAnchors.add(c.anchor); });
              if (defaultBranded && defaultAnchor) reservedAnchors.add(defaultAnchor);
              return (
                <OverlayConfigCard
                  key={config.overlayId}
                  overlay={overlay}
                  config={config}
                  reservedAnchors={reservedAnchors}
                  onChange={(updated) => updateConfig(i, updated)}
                  onRemove={() => removeConfig(i)}
                />
              );
            })}
          </div>
        )}

        {/* Default branding — owner opt-out, disclaimer for everyone else */}
        {isOwner ? (
          // Gated on `initialized` — `defaultBranded` is derived from the
          // fetched overlay list, so it would read false while loading.
          initialized && (
            <label className={styles['branding-box']}>
              <input
                type="checkbox"
                checked={defaultBranded}
                disabled={!defaultOverlay}
                onChange={(e) => toggleDefaultBranding(e.target.checked)}
                className={styles['branding-checkbox']}
                aria-label="Apply the default Pixel Machha watermark"
              />
              <p className={styles['branding-text']}>
                Apply the <strong>default Pixel Machha watermark</strong> to every photo in this album.
                As an owner you can untick this to skip the default branding for this album only.
                Saving re-processes the album&apos;s photos.
              </p>
            </label>
          )
        ) : (
          <div className={styles['branding-box']}>
            <span className={styles['branding-icon']}>&#9888;</span>
            <p className={styles['branding-text']}>
              A <strong>Pixel Machha watermark</strong> is automatically applied to all album photos during beta.
              You may request its removal by contacting us.
            </p>
          </div>
        )}
      </div>

      <div className={styles['tab-footer-split']}>
        <div>
          {overlayConfigs.length > 0 && (
            <Button
              variant="ghost"
              loading={loadingPreviewPhoto}
              onClick={async () => {
                if (previewPhoto) { setShowPreview(true); return; }
                setLoadingPreviewPhoto(true);
                try {
                  const r = await albumsApi.listPhotos(album._id);
                  if (r.photos.length > 0) {
                    setPreviewPhoto(r.photos[0]);
                    setShowPreview(true);
                  } else {
                    toast.error('No photos in album to preview');
                  }
                } catch {
                  toast.error('Failed to load preview photo');
                } finally {
                  setLoadingPreviewPhoto(false);
                }
              }}
            >
              <Eye style={{ width: 14, height: 14, marginRight: 6 }} />
              Preview
            </Button>
          )}
        </div>
        <Button onClick={handleSave} loading={saving}>
          Save Overlays
        </Button>
      </div>

      {previewPhoto && (
        <OverlayPreviewModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          photo={previewPhoto}
          overlayConfigs={overlayConfigs}
        />
      )}
    </div>
  );
}

// ─── Members tab ──────────────────────────────────────────────────────────────

function MembersTab({ album }: { album: Album }) {
  const [members, setMembers] = useState<AlbumMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [kickingId, setKickingId] = useState<string | null>(null);
  const [confirmKickId, setConfirmKickId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;
    albumsApi.listMembers(album._id)
      .then((r) => {
        setMembers(r.members);
        setInitialized(true);
      })
      .catch(() => toast.error('Failed to load members'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKick = async (userId: string) => {
    setKickingId(userId);
    try {
      await albumsApi.kickMember(album._id, userId);
      track(EVENTS.ALBUM_MEMBER_KICKED, { album_id: album._id, kicked_user_id: userId });
      setMembers((prev) => prev.filter((m) => m.userId._id !== userId));
      setConfirmKickId(null);
      toast.success('Member removed');
    } catch {
      toast.error('Failed to remove member');
    } finally {
      setKickingId(null);
    }
  };

  if (loading) {
    return (
      <div className={styles['member-skeleton-col']}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className={styles['member-skeleton-row']}>
            <Skeleton style={{ width: 36, height: 36, borderRadius: '50%' }} />
            <div className={styles['member-skeleton-info']}>
              <Skeleton style={{ height: 12, width: 128, borderRadius: 9999 }} />
              <Skeleton style={{ height: 8, width: 192, borderRadius: 9999 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className={styles['empty-state']} style={{ paddingTop: '1rem' }}>
        <MachhaCharacter state="Empty" size={100} />
        <p className={styles['empty-title']}>No one has joined yet</p>
        <p className={styles['empty-text-sm']}>
          Share the invite link to let people join this album.
        </p>
      </div>
    );
  }

  return (
    <div className={styles['members-col']}>
      {members.map((member) => {
        const isConfirming = confirmKickId === member.userId._id;
        const isKicking = kickingId === member.userId._id;

        return (
          <div
            key={member._id}
            className={styles['member-row']}
          >
            {/* Avatar */}
            {member.userId.picture ? (
              <img
                src={member.userId.picture}
                alt={member.userId.name}
                referrerPolicy="no-referrer"
                className={styles['member-avatar']}
              />
            ) : (
              <div className={styles['member-avatar-fallback']}>
                {member.userId.name.charAt(0).toUpperCase()}
              </div>
            )}

            {/* Info */}
            <div className={styles['member-info']}>
              <p className={styles['member-name']}>
                {member.userId.name}
              </p>
              <p className={styles['member-email']}>
                {member.userId.email}
              </p>
            </div>

            {/* Kick controls */}
            {isConfirming ? (
              <div className={styles['kick-controls']}>
                <span className={styles['kick-label']}>Remove?</span>
                <Button
                  variant="danger"
                  size="sm"
                  loading={isKicking}
                  onClick={() => handleKick(member.userId._id)}
                >
                  Kick
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmKickId(null)}
                  disabled={isKicking}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmKickId(member.userId._id)}
                className={styles['kick-icon-btn']}
                aria-label={`Kick ${member.userId.name}`}
                title="Remove from album"
              >
                <UserX style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Sources tab (live albums only) ───────────────────────────────────────────

function SourcesTab({ album, onUpdated }: { album: Album; onUpdated: (updated: Album) => void }) {
  const [sourceFilter, setSourceFilter] = useState<'all' | 'selected'>(album.sourceFilter ?? 'all');
  const [sourceFtpAccountIds, setSourceFtpAccountIds] = useState<string[]>(album.sourceFtpAccountIds ?? []);
  const [sourceIncludeManualUploads, setSourceIncludeManualUploads] = useState<boolean>(
    album.sourceIncludeManualUploads ?? true,
  );
  const [ftpAccounts, setFtpAccounts] = useState<FTPAccount[]>([]);
  const [loadingFtp, setLoadingFtp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;
    setLoadingFtp(true);
    ftpAccountsApi
      .list(album.eventId)
      .then(setFtpAccounts)
      .catch(() => toast.error('Failed to load FTP accounts'))
      .finally(() => setLoadingFtp(false));
    setInitialized(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFtp = (id: string) =>
    setSourceFtpAccountIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const isDirty =
    sourceFilter !== (album.sourceFilter ?? 'all') ||
    JSON.stringify([...sourceFtpAccountIds].sort()) !==
      JSON.stringify([...(album.sourceFtpAccountIds ?? [])].sort()) ||
    sourceIncludeManualUploads !== (album.sourceIncludeManualUploads ?? true);

  const canSave = sourceFilter === 'all' || sourceFtpAccountIds.length > 0 || sourceIncludeManualUploads;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await albumsApi.update(album._id, {
        sourceFilter,
        sourceFtpAccountIds: sourceFilter === 'selected' ? sourceFtpAccountIds : [],
        sourceIncludeManualUploads: sourceFilter === 'selected' ? sourceIncludeManualUploads : true,
      });
      onUpdated(updated);
      toast.success('Sources updated');
    } catch {
      toast.error('Failed to update sources');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles['tab-col']}>
      <div className={styles['tab-scroll']}>
        <p className={styles['source-desc']}>
          Control which photo sources automatically feed into this live album.
        </p>

        {/* Radio: all vs selected */}
        <div className={styles['radio-group']}>
          {(['all', 'selected'] as const).map((opt) => (
            <button
              key={opt}
              onClick={() => setSourceFilter(opt)}
              className={[
                styles['radio-btn'],
                sourceFilter === opt ? styles['radio-btn-selected'] : '',
              ].filter(Boolean).join(' ')}
            >
              <div className={styles['radio-btn-row']}>
                <div
                  className={[
                    styles['radio-dot'],
                    sourceFilter === opt ? styles['radio-dot-selected'] : '',
                  ].filter(Boolean).join(' ')}
                >
                  {sourceFilter === opt && <div className={styles['radio-dot-inner']} />}
                </div>
                <div>
                  <p className={styles['radio-label']}>
                    {opt === 'all' ? 'All sources' : 'Selected sources only'}
                  </p>
                  <p className={styles['radio-sublabel']}>
                    {opt === 'all'
                      ? 'All FTP accounts and manual uploads'
                      : 'Choose specific FTP accounts and/or manual uploads'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Selected-mode controls */}
        {sourceFilter === 'selected' && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className={styles['source-detail-col']}
          >
            {/* Manual uploads toggle */}
            <button
              onClick={() => setSourceIncludeManualUploads((v) => !v)}
              className={[
                styles['toggle-btn'],
                sourceIncludeManualUploads ? styles['toggle-btn-active'] : '',
              ].filter(Boolean).join(' ')}
            >
              <div
                className={[
                  styles['toggle-track'],
                  sourceIncludeManualUploads ? styles['toggle-track-on'] : styles['toggle-track-off'],
                ].join(' ')}
                style={{ width: 30, height: 18 }}
              >
                <div
                  className={[
                    styles['toggle-thumb'],
                    sourceIncludeManualUploads ? styles['toggle-thumb-on'] : styles['toggle-thumb-off'],
                  ].join(' ')}
                  style={{ width: 14, height: 14 }}
                />
              </div>
              <span className={styles['toggle-label']}>
                Include manual uploads
              </span>
            </button>

            {/* FTP accounts */}
            {loadingFtp ? (
              <div className={styles['skeleton-col']}>
                {[0, 1].map((i) => (
                  <div key={i} className={styles['skeleton-item']} />
                ))}
              </div>
            ) : ftpAccounts.length === 0 ? (
              <p className={styles['no-ftp-msg']}>
                No FTP accounts for this event.
              </p>
            ) : (
              <div className={styles['ftp-list']}>
                {ftpAccounts.map((acc) => {
                  const checked = sourceFtpAccountIds.includes(acc._id);
                  return (
                    <button
                      key={acc._id}
                      onClick={() => toggleFtp(acc._id)}
                      className={[
                        styles['toggle-btn'],
                        checked ? styles['toggle-btn-active'] : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div
                        className={[
                          styles['ftp-checkbox'],
                          checked ? styles['ftp-checkbox-checked'] : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {checked && <Check style={{ width: 10, height: 10, color: '#fff' }} strokeWidth={3} />}
                      </div>
                      <div className={styles['ftp-info']}>
                        <p className={styles['ftp-name']}>
                          {acc.photographerName}
                        </p>
                        <p className={styles['ftp-username']}>
                          {acc.username}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </div>

      <div className={styles['tab-footer']}>
        <Button onClick={handleSave} loading={saving} disabled={!isDirty || !canSave}>
          Save Sources
          <ChevronRight style={{ width: 14, height: 14 }} />
        </Button>
      </div>
    </div>
  );
}

// ─── Face Recognition tab ────────────────────────────────────────────────────

function FaceRecognitionTab({ album, onUpdated }: { album: Album; onUpdated: (a: Album) => void }) {
  const [enabled, setEnabled] = useState(album.faceRecognitionEnabled ?? false);
  const [visibility, setVisibility] = useState<'all' | 'matched_only'>(album.guestVisibility ?? 'all');
  const [scope, setScope] = useState<'album' | 'event'>(album.faceSearchScope ?? 'album');
  const [saving, setSaving] = useState(false);

  // Check if anything changed
  const hasChanges =
    enabled !== (album.faceRecognitionEnabled ?? false) ||
    visibility !== (album.guestVisibility ?? 'all') ||
    scope !== (album.faceSearchScope ?? 'album');

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await albumsApi.update(album._id, {
        faceRecognitionEnabled: enabled,
        guestVisibility: visibility,
        faceSearchScope: scope,
      });
      onUpdated(updated);
      toast.success(
        enabled
          ? 'Face recognition enabled — photo indexing will start shortly'
          : 'Face recognition disabled',
      );
    } catch {
      toast.error('Failed to update face recognition settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles['tab-col']}>
      <div className={styles['tab-scroll']}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Hero toggle */}
          <button
            type="button"
            className={[faceStyles['hero-card'], enabled ? faceStyles['hero-card-active'] : ''].filter(Boolean).join(' ')}
            onClick={() => setEnabled(!enabled)}
            role="switch"
            aria-checked={enabled}
          >
            <div className={[faceStyles['hero-icon-wrap'], enabled ? faceStyles['hero-icon-wrap-active'] : ''].filter(Boolean).join(' ')}>
              <ScanFace size={22} style={{ color: 'var(--clay-orange)' }} />
            </div>
            <div className={faceStyles['hero-body']}>
              <p className={faceStyles['hero-title']}>Enable Face Recognition</p>
              <p className={faceStyles['hero-subtitle']}>
                Let guests upload a selfie to instantly find every photo they appear in
              </p>
            </div>
            <div className={[faceStyles['toggle-track'], enabled ? faceStyles['toggle-track-on'] : faceStyles['toggle-track-off']].join(' ')}>
              <div className={[faceStyles['toggle-thumb'], enabled ? faceStyles['toggle-thumb-on'] : faceStyles['toggle-thumb-off']].join(' ')} />
            </div>
          </button>

          {enabled && (
            <>
              {/* Guest visibility */}
              <div>
                <p className={faceStyles['section-header']}>Guest visibility</p>
                <div className={faceStyles.options}>
                  {([
                    { id: 'all' as const, icon: <Eye size={15} style={{ color: 'var(--clay-orange)' }} />, label: 'All photos + My Photos filter', desc: 'Guests browse every photo, plus a "My Photos" tab.', tag: 'Open galleries' },
                    { id: 'matched_only' as const, icon: <UserX size={15} style={{ color: 'var(--clay-orange)' }} />, label: 'Only their photos + highlights', desc: 'Guests see ONLY photos they appear in, plus pinned highlights.', tag: 'Personal delivery' },
                  ] as const).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={[faceStyles.option, visibility === opt.id ? faceStyles['option-selected'] : ''].filter(Boolean).join(' ')}
                      onClick={() => setVisibility(opt.id)}
                    >
                      <div className={[faceStyles.radio, visibility === opt.id ? faceStyles['radio-selected'] : ''].filter(Boolean).join(' ')}>
                        {visibility === opt.id && <div className={faceStyles['radio-dot']} />}
                      </div>
                      <div className={faceStyles['option-body']}>
                        <div className={faceStyles['option-label-row']}>
                          {opt.icon}
                          <span className={faceStyles['option-label']}>{opt.label}</span>
                        </div>
                        <p className={faceStyles['option-desc']}>{opt.desc}</p>
                        <span className={faceStyles['option-tag']}>{opt.tag}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Search scope — only for non-live albums */}
              {!album.isLive && (
                <div>
                  <p className={faceStyles['section-header']}>Search scope</p>
                  <div className={faceStyles.options}>
                    {([
                      { id: 'album' as const, label: 'This album only', desc: 'Match faces against photos selected for this album.', tag: 'Curated albums' },
                      { id: 'event' as const, label: 'All event photos', desc: 'Match faces against every photo from the event.', tag: 'Full coverage' },
                    ] as const).map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        className={[faceStyles.option, scope === opt.id ? faceStyles['option-selected'] : ''].filter(Boolean).join(' ')}
                        onClick={() => setScope(opt.id)}
                      >
                        <div className={[faceStyles.radio, scope === opt.id ? faceStyles['radio-selected'] : ''].filter(Boolean).join(' ')}>
                          {scope === opt.id && <div className={faceStyles['radio-dot']} />}
                        </div>
                        <div className={faceStyles['option-body']}>
                          <span className={faceStyles['option-label']}>{opt.label}</span>
                          <p className={faceStyles['option-desc']}>{opt.desc}</p>
                          <span className={faceStyles['option-tag']}>{opt.tag}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Save button */}
      {hasChanges && (
        <div className={styles['tab-footer']}>
          <Button onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Edit modal root ──────────────────────────────────────────────────────────

interface AlbumEditModalProps {
  album: Album | null;
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (updated: Album) => void;
}

export function AlbumEditModal({ album, eventId, isOpen, onClose, onUpdated }: AlbumEditModalProps) {
  const [activeTab, setActiveTab] = useState<EditTab>('info');

  // Reset to info tab when a different album is opened
  useEffect(() => {
    if (isOpen) setActiveTab('info'); // eslint-disable-line react-hooks/set-state-in-effect
  }, [isOpen, album?._id]);

  if (!album) return null;

  // Augment album.eventId from prop when not on album object
  const albumWithEventId = { ...album, eventId: album.eventId || eventId };
  const tabs = album.isLive ? LIVE_TABS : NORMAL_TABS;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Edit \u2014 ${album.name}`} size="xl">
      <div className={styles['modal-body']}>
        <EditTabStepper
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

        {activeTab === 'info' && (
          <InfoTab album={albumWithEventId} onUpdated={onUpdated} />
        )}
        {activeTab === 'photos' && !album.isLive && (
          <PhotosTab album={albumWithEventId} onUpdated={onUpdated} />
        )}
        {activeTab === 'cover' && (
          <CoverTab album={albumWithEventId} onUpdated={onUpdated} />
        )}
        {activeTab === 'overlays' && (
          <OverlaysTab album={albumWithEventId} onUpdated={onUpdated} />
        )}
        {activeTab === 'face' && (
          <FaceRecognitionTab album={albumWithEventId} onUpdated={onUpdated} />
        )}
        {activeTab === 'sources' && album.isLive && (
          <SourcesTab album={albumWithEventId} onUpdated={onUpdated} />
        )}
        {activeTab === 'analytics' && album.isLive && (
          <AnalyticsTab album={albumWithEventId} />
        )}
        {activeTab === 'slideshow' && album.isLive && (
          <SlideshowSettingsTab album={albumWithEventId} onUpdated={onUpdated} />
        )}
        {activeTab === 'members' && (
          <MembersTab album={albumWithEventId} />
        )}
        {activeTab === 'reports' && (
          <ReportsPanel albumId={album._id} />
        )}
      </div>
    </Modal>
  );
}
